import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion";

interface XPAnnouncerContextValue {
  announceXP: (amount: number) => void;
}

const XPAnnouncerContext = createContext<XPAnnouncerContextValue | null>(null);

interface FloatingXP {
  id: number;
  amount: number;
}

const FLOAT_DURATION_MS = 1200;

/**
 * App-shell-level provider that exposes `announceXP(n)` to descendants.
 *
 * Renders two siblings:
 *  - a visually-hidden `aria-live="polite"` region the screen reader speaks
 *    ("+25 XP earned") without taking focus.
 *  - a fixed-position visual stack of "+N XP" floats, animated upward via
 *    motion. Suppressed under `prefers-reduced-motion`, replaced by a brief
 *    static badge so the visual cue still confirms the action.
 */
export const XPAnnouncerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [floats, setFloats] = useState<FloatingXP[]>([]);
  // Storing both the text AND a counter id forces a new object reference on
  // every announcement. Without it, React's useState bails on Object.is for
  // identical strings (e.g. two "+25 XP earned" awards back-to-back) and the
  // live region's text content never changes — so the screen reader has
  // nothing to re-announce.
  const [announcement, setAnnouncement] = useState<{ text: string; id: number } | null>(null);
  const counter = useRef(0);
  const floatTimeoutsRef = useRef<Set<number>>(new Set());
  const reducedMotion = usePrefersReducedMotion();

  const announceXP = useCallback(
    (amount: number) => {
      if (!Number.isFinite(amount) || amount <= 0) return;
      const id = ++counter.current;
      setFloats((prev) => [...prev, { id, amount }]);
      // Toggle to null first so the live region re-fires even when the new
      // "+N XP earned" string is identical to the previous one (React's
      // useState bails on Object.is for identical strings, so the DOM never
      // changes and the SR has nothing to re-announce).
      // Schedule the new value on a macrotask so React commits the empty
      // render BEFORE the new text — a microtask would batch with the null
      // update and the intermediate empty state would never reach the DOM.
      setAnnouncement(null);
      const reSetTimer = window.setTimeout(() => {
        setAnnouncement({ text: `+${amount} XP earned`, id });
        floatTimeoutsRef.current.delete(reSetTimer);
      }, 0);
      floatTimeoutsRef.current.add(reSetTimer);

      const timeout = window.setTimeout(
        () => {
          setFloats((prev) => prev.filter((f) => f.id !== id));
          floatTimeoutsRef.current.delete(timeout);
        },
        reducedMotion ? 600 : FLOAT_DURATION_MS
      );
      floatTimeoutsRef.current.add(timeout);
    },
    [reducedMotion]
  );

  // Clear the live region a tick after the announcement so an identical
  // subsequent value would be re-announced (some SRs dedupe identical text).
  useEffect(() => {
    if (!announcement) return;
    const t = window.setTimeout(() => setAnnouncement(null), 1500);
    return () => window.clearTimeout(t);
  }, [announcement]);

  // Cancel any pending float-removal timers on unmount so they don't try to
  // setFloats on an unmounted provider.
  useEffect(
    () => () => {
      floatTimeoutsRef.current.forEach((id) => window.clearTimeout(id));
      floatTimeoutsRef.current.clear();
    },
    []
  );

  const value = useMemo(() => ({ announceXP }), [announceXP]);

  return (
    <XPAnnouncerContext.Provider value={value}>
      {children}
      <div aria-live="polite" aria-atomic="true" className="sr-only" data-testid="xp-live-region">
        {announcement?.text ?? ""}
      </div>
      <div
        aria-hidden="true"
        data-testid="xp-float-stack"
        className="pointer-events-none fixed bottom-24 right-6 z-50 flex flex-col items-end gap-2"
      >
        <AnimatePresence>
          {floats.map((f) => (
            <motion.div
              key={f.id}
              data-testid={`xp-float-${f.id}`}
              initial={reducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 12, scale: 0.9 }}
              animate={
                reducedMotion ? { opacity: 1, y: 0 } : { opacity: [0, 1, 1, 0], y: -40, scale: 1 }
              }
              exit={{ opacity: 0 }}
              transition={{ duration: reducedMotion ? 0 : FLOAT_DURATION_MS / 1000 }}
              className="rounded-full bg-primary px-3 py-1 text-sm font-semibold text-primary-foreground shadow"
            >
              +{f.amount} XP
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </XPAnnouncerContext.Provider>
  );
};

export function useXPAnnouncer(): XPAnnouncerContextValue {
  const ctx = useContext(XPAnnouncerContext);
  if (!ctx) {
    // Fallback no-op so components don't crash if mounted outside the
    // provider (e.g. in unit tests that render the dialog in isolation).
    return { announceXP: () => {} };
  }
  return ctx;
}
